import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../api/errors';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useDocuments } from '../../documents/api/useDocuments';
import { useMachines } from '../../machines/api/useMachines';
import { askQuestion, createConversation } from '../api/conversations.api';
import { ConversationComposer } from '../components/ConversationComposer';
import { ThinkingState } from '../components/ThinkingState';
import { conversationTitle } from '../types/conversation';

const suggestions = [
  'What are the most common maintenance issues?',
  'What patterns appear in the maintenance history?',
  'What does the available evidence say I should pay attention to?',
  'How complete is the available maintenance information?',
];

export function FreshConversationPage() {
  const { machineId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const machines = useMachines();
  const documents = useDocuments(machineId, auth.can(permissions.documentsRead));
  const machine = machines.data?.machines.find((item) => item.id === machineId);
  const documentCount = documents.data?.documents.length ?? 0;
  const readyDocuments = documents.data?.documents.filter((document) => document.questionReady) ?? [];
  const readyCount = readyDocuments.length;
  const learningCount = documents.data?.documents.filter((document) => document.uiStatus === 'learning' || document.uiStatus === 'uploading').length ?? 0;
  const documentScopeId = searchParams.get('document');
  const seededPrompt = searchParams.get('prompt') ?? '';
  const firstMachineOnboarding = searchParams.get('onboarding') === 'first-machine';
  const scopedDocument = useMemo(() => readyDocuments.find((document) => document.id === documentScopeId) ?? null, [documentScopeId, readyDocuments]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null);

  const canAsk = auth.can(permissions.conversationCreate) && auth.can(permissions.conversationRead) && auth.can(permissions.questionAsk) && readyCount > 0;

  async function submit(question: string) {
    if (!canAsk) return;
    setBusy(true);
    setError(null);
    try {
      let conversationId = pendingConversationId;
      if (!conversationId) {
        const created = await createConversation(machineId, conversationTitle(question));
        conversationId = created.conversation.conversation_id;
        setPendingConversationId(conversationId);
      }
      await askQuestion(conversationId, question, scopedDocument ? [scopedDocument.id] : undefined);
      await queryClient.invalidateQueries({ queryKey: queryKeys.conversations(auth.activeCustomerId, machineId) });
      navigate(`/machines/${machineId}/conversations/${conversationId}`);
    } catch (value) {
      setError(questionError(value));
      throw value;
    } finally {
      setBusy(false);
    }
  }

  function clearScope() {
    const next = new URLSearchParams(searchParams);
    next.delete('document');
    setSearchParams(next, { replace: true });
  }

  if (machines.isPending || documents.isPending) {
    return (
      <section className="r42-conversation-page">
        <div className="r42-conversation-bootstrap" data-testid="conversation-loading" role="status" aria-live="polite">
          <span className="r42-learning-dot" aria-hidden="true" />
          <div>
            <strong>Loading machine conversation…</strong>
            <span>Route42 is checking the latest grounded machine knowledge.</span>
          </div>
        </div>
      </section>
    );
  }

  if (machines.isError || documents.isError) {
    return (
      <section className="r42-conversation-page">
        <div className="r42-inline-state r42-inline-state--error">
          <span>Route42 couldn't load this machine's conversation workspace.</span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => void Promise.all([machines.refetch(), documents.refetch()])}
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="r42-conversation-page">
      <header className="r42-work-head">
        <div>
          <h1>{machine?.name ?? 'Machine workspace'}</h1>
          <p>{[machine?.machineType, machine?.manufacturer, machine?.model, machine?.location].filter(Boolean).join(' • ') || 'Route42 machine intelligence'}</p>
        </div>
        <span className="r42-intel-status">Machine Intelligence <b>{intelligenceLabel(documentCount, readyCount, learningCount)}</b></span>
      </header>

      {scopedDocument ? (
        <div className="r42-scope-bar"><i className="bi bi-file-earmark-text" aria-hidden="true" /><span>Context: <strong>{scopedDocument.filename}</strong></span><button onClick={clearScope}>Clear</button></div>
      ) : null}

      <div className="r42-conversation-scroll">
        <div className="r42-conversation-empty">
          <span className="r42-eyebrow">Conversation workspace</span>
          <h2>{documentCount === 0 ? 'Give Route42 something to learn from.' : scopedDocument ? 'What would you like to know about this document?' : 'What would you like to know about this machine?'}</h2>
          {documentCount === 0 ? (
            <>
              <p>Add machine-specific documents such as manuals, maintenance history, inspection reports, technician notes, or spreadsheets. Route42 will begin building usable machine knowledge as soon as the uploads arrive.</p>
              {auth.can(permissions.documentsUpload) ? (
                <div className="r42-conversation-primary-action">
                  <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}><i className="bi bi-file-earmark-arrow-up" aria-hidden="true" /> Add documents</Link>
                  <Link className="r42-text-link" to={`/machines/${machineId}/documents`}>Open document library</Link>
                </div>
              ) : null}
            </>
          ) : readyCount === 0 ? (
            <>
              <p>Route42 is still learning from the uploaded information. Questions activate as soon as grounded machine knowledge becomes available.</p>
              <Link className="r42-conversation-doc-link" to={`/machines/${machineId}/documents`}><i className="bi bi-file-earmark-text" aria-hidden="true" /> Check document learning status</Link>
            </>
          ) : (
            <>
              <p>{scopedDocument ? 'Route42 will use only grounded evidence from this document until you clear the context.' : `${readyCount} ${readyCount === 1 ? 'document is' : 'documents are'} ready for grounded questions.${learningCount > 0 ? ` Route42 is still learning from ${learningCount} more.` : ''}`}</p>
              {firstMachineOnboarding ? (
                <div className="r42-first-question-prompt">
                  <span><i className="bi bi-check-lg" aria-hidden="true" /> Knowledge ready</span>
                  <strong>Step 3: Ask your first question</strong>
                  <p>Use a question your team would normally have to answer by searching through machine history.</p>
                  <button
                    className="btn r42-btn-primary"
                    type="button"
                    onClick={() => document.querySelector<HTMLTextAreaElement>('.r42-composer textarea')?.focus()}
                  >
                    Ask your first question <i className="bi bi-arrow-down" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
              <div className="r42-suggestions">
                {suggestions.map((suggestion) => <button key={suggestion} disabled={busy || !canAsk} onClick={() => void submit(suggestion).catch(() => undefined)}>{suggestion}</button>)}
              </div>
              <Link className="r42-conversation-doc-link" to={`/machines/${machineId}/documents`}><i className="bi bi-file-earmark-text" aria-hidden="true" /> View {documentCount} {documentCount === 1 ? 'document' : 'documents'}</Link>
            </>
          )}
          {busy ? <ThinkingState /> : null}
          {error ? <div className="r42-question-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><span>{error}</span></div> : null}
        </div>
      </div>
      <div className="r42-composer-wrap">
        <ConversationComposer machineName={machine?.name ?? 'this machine'} disabled={!canAsk} busy={busy} initialValue={seededPrompt} onSubmit={submit} />
        <div id="r42-mobile-ace-launcher-slot" className="r42-mobile-ace-launcher-slot" />
        <p>{readyCount > 0 ? (scopedDocument ? `Grounded only to ${scopedDocument.filename}.` : 'Answers use the latest grounded machine evidence available to Route42.') : 'Questions activate when grounded machine knowledge is ready.'}</p>
      </div>
    </section>
  );
}

function intelligenceLabel(documentCount: number, readyCount: number, learningCount: number): string {
  if (documentCount === 0) return 'Limited';
  if (learningCount > 0) return 'Updating';
  if (readyCount > 0) return 'Available';
  return 'Limited';
}

function questionError(value: unknown): string {
  if (value instanceof ApiError) {
    if (value.code === 'MACHINE_KNOWLEDGE_NOT_READY' || value.code === 'MACHINE_MEMORY_NOT_READY') return 'Route42 is still learning this machine. Grounded questions will become available as soon as usable evidence is ready.';
    if (value.code === 'GROUNDED_ANSWER_INVALID') return 'Route42 could not verify the grounding for that answer, so it was not shown. Try asking the question another way.';
    return value.message;
  }
  return 'Route42 could not answer that question. Please try again.';
}
